import { cn } from 'lib/utils'
import * as React from 'react'

interface TypingDotsProps {
  /** Color of the dots. Defaults to acid for "AI is doing work"; use `paper-dim` for "user is typing". */
  tone?: 'acid' | 'paper-dim'
  /** Pixel size of each dot. Defaults to 5px. */
  size?: number
  className?: string
}

/**
 * Three staggered bouncing dots — the universal "thinking..." indicator for
 * chat, generation, and any other in-progress moment that needs to feel
 * alive without claiming a definite progress amount.
 *
 * Replaces the inline `animate-typing-dot` spans scattered across SideChat,
 * AssistantChat, HomePage, and the composer mobile bar. The keyframe
 * (`typing-bounce` in index.css) is global and reused.
 *
 * Spec: playbook/design-system/components.md#typing-dots.
 *
 * Examples:
 *
 *   <TypingDots />              // acid, 5px — "Taka is writing..."
 *   <TypingDots tone="paper-dim" size={4} />   // user is typing in chat
 */
export const TypingDots: React.FC<TypingDotsProps> = ({ tone = 'acid', size = 5, className }) => {
  // Bind dot color to a stable token. Acid is the brand "doing work" hit;
  // paper-dim is the "someone's typing" passive hit.
  const dotClass = tone === 'acid' ? 'bg-acid' : 'bg-[color:var(--paper-dim)]'
  const style = { width: size, height: size }
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn('inline-flex items-center gap-[3px]', className)}
    >
      <span
        className={cn('animate-typing-dot rounded-full', dotClass)}
        style={{ ...style, animationDelay: '0ms' }}
      />
      <span
        className={cn('animate-typing-dot rounded-full', dotClass)}
        style={{ ...style, animationDelay: '150ms' }}
      />
      <span
        className={cn('animate-typing-dot rounded-full', dotClass)}
        style={{ ...style, animationDelay: '300ms' }}
      />
    </span>
  )
}
